import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { fetchSessions } from '../Utils/SessionUtils';
import { populateTagOptionsSupabase } from '../Utils/TagUtils';
import { 
  loadUserByToken, 
  saveUserData, 
  mapFormToUserData, 
  generateUniqueToken,
  createEditLink
} from '../Utils/UserUtils';

/**
 * Custom hook for handling registration form logic
 * @param {Object} options - Configuration options
 * @returns {Object} - Form state and handler functions
 */
export const useRegistration = ({ editToken, venueId }) => {
  const navigate = useNavigate();
  
  // Form handling with React Hook Form
  const formMethods = useForm();
  const { register, handleSubmit, formState, reset, control, setValue } = formMethods;
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // User preferences state
  const [optInStats, setOptInStats] = useState(false);
  const [optInVideo, setOptInVideo] = useState(false);
  const [optInSocials, setOptInSocials] = useState(false);
  
  // Sessions and tag state
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [tagOptions, setTagOptions] = useState([]);
  const [tagMappings, setTagMappings] = useState({});
  
  // Edit mode state
  const [isUpdate, setIsUpdate] = useState(false);
  const [userId, setUserId] = useState(null);
  const [editLink, setEditLink] = useState('');
  const [showEditLinkDialog, setShowEditLinkDialog] = useState(false);
  
  // Store user's venue ID if editing
  const [userVenueId, setUserVenueId] = useState(null);
  
  // Use current venue ID or user's venue ID if available
  const effectiveVenueId = userVenueId || venueId;
  
  // Load tags and sessions on component mount
  useEffect(() => {
    console.log("Loading tags and sessions for venue", effectiveVenueId);
    const loadData = async () => {
      // Load tags with venueId
      const tagResult = await populateTagOptionsSupabase(effectiveVenueId);
      setTagOptions(tagResult.options);
      setTagMappings(tagResult.mappings);
      
      // Load sessions filtered by venue_id if provided
      const sessionsData = await fetchSessions(effectiveVenueId);
      setSessions(sessionsData);
    };
    
    if (effectiveVenueId) {
      loadData();
    }
  }, [effectiveVenueId]);
  
  // Check for edit token on component mount
  useEffect(() => {
    if (editToken) {
      handleLoadUserByToken(editToken);
    }
  }, [editToken]);
  
  // Helper to create registration URL with appropriate venue
  const getRegistrationUrl = () => {
    if (effectiveVenueId) {
      return `/register?venue_id=${effectiveVenueId}`;
    }
    return '/register/gent';
  };
  
  // Handler for loading user data by edit token
  const handleLoadUserByToken = async (token) => {
    setLoading(true);
    try {
      const { user, error } = await loadUserByToken(token);
      
      if (error) {
        setSubmitError(error);
        return;
      }
      
      if (user) {
        // Load tags first to ensure we can find matching display format
        const userVenueId = user.venue_id || effectiveVenueId;
        const tagResult = await populateTagOptionsSupabase(userVenueId);
        setTagOptions(tagResult.options);
        setTagMappings(tagResult.mappings);
        
        // Find the full tag display format if it exists in options
        let tagDisplay = user.tag || '';
        
        // Look for the display format that corresponds to this tag
        const foundDisplayFormat = Object.entries(tagResult.mappings).find(
          ([display, tag]) => tag === user.tag
        );
        
        if (foundDisplayFormat) {
          tagDisplay = foundDisplayFormat[0]; // Use the display format (tag: name)
        }
        
        // Set form values
        setValue('firstName', user.first_name);
        setValue('lastName', user.last_name);
        setValue('alias', user.alias || '');
        setValue('email', user.email);
        setValue('birthDate', user.birth_date || '');
        setValue('mylapsTag', tagDisplay);
        setValue('instagram_handle', user.instagram_handle || '');
        
        // Set checkbox values
        setOptInStats(user.opt_in_stats || false);
        setOptInVideo(user.opt_in_video || false);
        setOptInSocials(user.opt_in_socials || false);
        
        // Load sessions and find matching session
        // Use the user's venue_id if available, otherwise use the current venueId
        const sessions = await fetchSessions(userVenueId);
        setSessions(sessions);
        
        // Find the matching session by id and set it as selected
        const matchingSession = sessions.find(s => s.id === user.session_id);
        if (matchingSession) {
          setSelectedSession(matchingSession);
        }
        
        setUserId(user.id);
        setIsUpdate(true);
        
        // Store the edit link for potential display after update
        // Use the user's original venue_id for the edit link
        setEditLink(createEditLink(token, userVenueId));
        
        // Store user's venue ID
        setUserVenueId(userVenueId);
      }
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handler for form submission
  const handleFormSubmit = async (formData) => {
    // Check if no video option is selected
    if (!optInVideo) {
      const confirmNoVideo = window.confirm(
        'Door deze keuze wordt er geen beeldmateriaal opgenomen van de hele sessie, voor geen enkele renner. Ben je zeker van je keuze?'
      );
      if (!confirmNoVideo) {
        return;
      }
    }
    
    // Validate session is selected
    if (!selectedSession) {
      setSubmitError('Selecteer een sessie');
      return;
    }
    
    // If the entered tag is not among the known options, confirm with the user
    if (!tagMappings[formData.mylapsTag]) {
      const confirmCustom = window.confirm(
        `De MyLaps tag "${formData.mylapsTag}" is niet gekend in onze database. Als dit niet je eigen tag is, ga dan terug om de juiste te kiezen.`
      );
      if (!confirmCustom) {
        return;
      }
    }
    
    setLoading(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    
    try {
      // Generate a unique token for editing if this is a new registration
      const newEditToken = !isUpdate ? generateUniqueToken() : null;
      
      // Prepare user data
      const userData = mapFormToUserData(formData, {
        sessionId: selectedSession.id,
        optInStats,
        optInVideo,
        optInSocials,
        editToken: newEditToken,
        tagMappings,
        venueId: effectiveVenueId
      });
      
      // Save user data
      const { data, error } = await saveUserData(userData, isUpdate ? userId : null);
      
      if (error) {
        throw new Error(error);
      }
      
      setSubmitSuccess(true);
      
      if (!isUpdate) {
        // For new registrations, show the edit link
        const newEditLink = createEditLink(newEditToken, effectiveVenueId);
        setEditLink(newEditLink);
        setShowEditLinkDialog(true);
        
        // Reset form after successful new registration
        reset();
        setOptInStats(false);
        setOptInVideo(false);
        setOptInSocials(false);
        setSelectedSession('');
      } else {
        // Get venue ID either from the existing user or current venue
        const userVenueId = data.venue_id || effectiveVenueId;
        
        // For updates, show the existing edit link before redirecting
        setShowEditLinkDialog(true);
        
        // After a brief delay, redirect to the registration page
        setTimeout(() => {
          navigate(getRegistrationUrl());
        }, 5000); // Redirect after 5 seconds. Edit link popup should stay open.
        
        setIsUpdate(false);
        setUserId(null);
      }
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handler for form reset
  const handleReset = () => {
    reset();
    setOptInStats(false);
    setOptInVideo(false);
    setOptInSocials(false);
    setSelectedSession('');
    setIsUpdate(false);
    setUserId(null);
    setSubmitSuccess(false);
    setSubmitError(null);
    
    // If we were editing via token, return to the main registration page
    if (editToken) {
      navigate(getRegistrationUrl());
    }
  };
  
  // Helper function to copy edit link to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(editLink)
      .then(() => {
        console.log('Link copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
      });
  };
  
  // Handler for closing the edit link dialog
  const handleCloseEditLinkDialog = () => {
    setShowEditLinkDialog(false);
    
    // If we're updating, navigate to main registration page after closing dialog
    if (isUpdate) {
      navigate(getRegistrationUrl());
    }
  };
  
  return {
    // Form methods
    register,
    handleSubmit: handleSubmit(handleFormSubmit),
    formState,
    control,
    
    // Form state
    loading,
    submitError,
    submitSuccess,
    
    // User preferences
    optInStats,
    setOptInStats,
    optInVideo,
    setOptInVideo,
    optInSocials,
    setOptInSocials,
    
    // Sessions and tags
    sessions,
    selectedSession,
    setSelectedSession,
    tagOptions,
    
    // Edit mode
    isUpdate,
    editLink,
    showEditLinkDialog,
    
    // Handlers
    handleReset,
    copyToClipboard,
    handleCloseEditLinkDialog
  };
}; 